'use client';
import React, { useState, useEffect } from 'react';
import { differenceInSeconds, differenceInDays, intervalToDuration } from 'date-fns';
import { classNames } from '@repo/ui/src/utils/classNames';
import Section from '@repo/ui/src/components/Section';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import AnimatedSmokeImages from '@components/CountdownComponent/_AnimatedSmokeImages';
import PostTimerBlock from '@components/CountdownComponent/_PostTimerBlock';
import TimerBlock from '@components/CountdownComponent/_TimerBlock';
import { LinkProps } from '@repo/ui/src/types/link';
import { getTZDate } from '@repo/ui/src/components/SiteWideBanner/_CountdownClock';

interface CountdownTimerProps {
  countdownClock: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  };
  darkMode?: boolean;
  title?: string;
  description?: string;
  postDescription?: string;
  link?: LinkProps;
}

interface TimeSegment {
  value: string;
  type: string;
}

const CountdownComponent = ({
  darkMode = true,
  description,
  title,
  link,
  postDescription,
  countdownClock = {},
}: CountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [prevTimeLeft, setPrevTimeLeft] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const { endDate, endTime, startDate, startTime } = countdownClock;

  const end = endDate && endTime !== undefined ? getTZDate(`${endDate} ${endTime}`) : null;
  const now = new Date();

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (!end || now >= end) {
      setTimeLeft(0);
      setIsAnimating(true);
    } else {
      const totalSeconds = differenceInSeconds(end, now);
      setTimeLeft(totalSeconds);

      interval = setInterval(() => {
        setTimeLeft((prevTimeLeft) => {
          setPrevTimeLeft(prevTimeLeft);
          if (prevTimeLeft <= 0) {
            clearInterval(interval);
            setIsAnimating(true);
            return 0;
          }
          return prevTimeLeft - 1;
        });
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [startDate, startTime, endDate, endTime]);

  const formatTimeSegments = React.useCallback((seconds: number): TimeSegment[] => {
    if (seconds <= 0) {
      return [
        { value: '00', type: 'hours' },
        { value: '00', type: 'minutes' },
        { value: '00', type: 'seconds' },
      ];
    }

    const now = new Date();
    const futureDate = new Date(now.getTime() + seconds * 1000);

    const totalDays = differenceInDays(futureDate, now);
    const duration = intervalToDuration({ start: now, end: futureDate });

    const segments = [];

    if (totalDays > 0) {
      segments.push({ value: totalDays.toString(), type: 'days' });
    }
    if ((duration?.hours ?? 0) > 0 || totalDays > 0) {
      segments.push({
        value: (duration?.hours ?? 0).toString().padStart(2, '0'),
        type: 'hours',
      });
    }
    segments.push({ value: (duration.minutes || 0).toString().padStart(2, '0'), type: 'minutes' });
    segments.push({ value: (duration.seconds || 0).toString().padStart(2, '0'), type: 'seconds' });

    return segments;
  }, []);

  const currentSegments = React.useMemo(
    () => formatTimeSegments(timeLeft),
    [timeLeft, formatTimeSegments],
  );

  const prevSegments = React.useMemo(
    () => formatTimeSegments(prevTimeLeft),
    [prevTimeLeft, formatTimeSegments],
  );
  const smokeImgUrl = buildImageSrc({ endpoint: 'traeger', name: 'transparent-smoke' });
  return (
    <Section
      dark={darkMode}
      texture
      className={classNames(
        'tw-transition-colors !tw-z-0 !tw-overflow-y-hidden',
        darkMode ? 'tw-text-white' : 'tw-text-gray-800',
      )}
    >
      <div className="tw-grid tw-min-h-[464px] tw-grid-areas-overlap tw-h-full tw-items-center tw-justify-center tw-text-center">
        <TimerBlock
          darkMode={darkMode}
          currentSegments={currentSegments}
          prevSegments={prevSegments}
          description={description}
          className="tw-grid-in-a"
          isAnimating={isAnimating}
        />

        <PostTimerBlock
          title={title}
          darkMode={darkMode}
          postDescription={postDescription}
          isAnimating={isAnimating}
          link={link}
          className="tw-grid-in-a"
        />
      </div>
      <AnimatedSmokeImages smokeImgUrl={smokeImgUrl} isAnimating={isAnimating} />
    </Section>
  );
};

export default CountdownComponent;
